import { writable } from "svelte/store";
import { authStore } from "$lib/stores";
import { get } from "svelte/store";

export interface SearchPreferences {
  proMode: boolean;
  searchAlgoVersion: "v3" | "v3.5";
  useFindAll: boolean;
}

const DEFAULT_PREFERENCES: SearchPreferences = {
  proMode: false,
  searchAlgoVersion: "v3",
  useFindAll: false,
};

function initializePreferences(): SearchPreferences {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;

  const stored = localStorage.getItem("searchPreferences");
  if (!stored) return DEFAULT_PREFERENCES;

  try {
    const parsed = JSON.parse(stored);
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch (error) {
    console.error("Failed to parse searchPreferences from localStorage:", error);
    return DEFAULT_PREFERENCES;
  }
}

function createSearchPreferencesStore() {
  const store = writable<SearchPreferences>(initializePreferences());
  const { subscribe, set, update } = store;

  subscribe((prefs) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("searchPreferences", JSON.stringify(prefs));
    }
  });

  function getSearchAlgoVersion(): "v3" | "v3.5" {
    const currentPrefs = get(store);
    if (authStore.isAdmin()) {
      return currentPrefs.searchAlgoVersion;
    } else {
      if (currentPrefs.proMode) {
        return "v3.5";
      } else {
        return "v3";
      }
    }
  }

  function getPublicSearchLabel(searchAlgoVersion?: string) {
    const algo = searchAlgoVersion ?? getSearchAlgoVersion();
    switch (algo) {
      case "v3":
        return "v3";
      case "v3.5":
        return authStore.isAdmin() ? "v4 Beta" : "v4 Beta (Pro Only)";
    }
  }

  function setProModeValue(options?: { default?: boolean; value?: boolean }) {
    const auth = get(authStore);

    if (options?.default) {
      if (auth.user?.plan === "pro") {
        update((prefs) => ({ ...prefs, proMode: true }));
      } else if (auth.user?.plan == "plus" && (auth.user?.proModeRemaining || 0) > 0) {
        update((prefs) => ({ ...prefs, proMode: true }));
      } else {
        update((prefs) => ({ ...prefs, proMode: false }));
      }
    } else {
      const newValue = options?.value ?? false;
      update((prefs) => ({ ...prefs, proMode: newValue }));
    }
  }

  return {
    subscribe,
    set,
    getSearchAlgoVersion,
    setProModeValue,
    getPublicSearchLabel,
    updatePreference<K extends keyof SearchPreferences>(key: K, value: SearchPreferences[K]) {
      update((prefs) => ({ ...prefs, [key]: value }));
    },
    reset() {
      set(DEFAULT_PREFERENCES);
    },
  };
}

const searchPreferences = createSearchPreferencesStore();
export { searchPreferences };
export default searchPreferences;
